import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { formatLocalDate } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { DollarSign, Search, X, Loader2, TrendingUp, TrendingDown, ArrowDownUp, RefreshCw, Wallet } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import type { BalanceSheetTransaction } from "@shared/schema";

const formatCurrency = (amount: string | number | null | undefined): string => {
  const num = parseFloat(String(amount || "0"));
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(num);
};

const formatDate = (date: string | Date | null | undefined): string =>
  formatLocalDate(date, { year: "numeric", month: "short", day: "numeric" });

const getSourceBadge = (source: string) => {
  switch (source) {
    case "budget_app":
      return <Badge variant="secondary" className="bg-purple-500/10 text-purple-600">Budget App</Badge>;
    case "invoice_payment":
      return <Badge variant="secondary" className="bg-green-500/10 text-green-600">Invoice Payment</Badge>;
    case "commission_payment":
      return <Badge variant="secondary" className="bg-blue-500/10 text-blue-600">Commission</Badge>;
    case "accounts_payable":
      return <Badge variant="secondary" className="bg-orange-500/10 text-orange-600">Payable</Badge>;
    default:
      return <Badge variant="secondary">{source}</Badge>;
  }
};

const getTypeBadge = (type: string) => {
  if (type === "income") {
    return <Badge className="bg-green-500/10 text-green-600 border-green-500/20"><TrendingUp className="w-3 h-3 mr-1" />Income</Badge>;
  }
  return <Badge className="bg-red-500/10 text-red-600 border-red-500/20"><TrendingDown className="w-3 h-3 mr-1" />Expense</Badge>;
};

export function BalanceSheetTab() {
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [yearFilter, setYearFilter] = useState<string>(new Date().getFullYear().toString());

  const { data: transactions = [], isLoading, refetch, isRefetching } = useQuery<BalanceSheetTransaction[]>({
    queryKey: ["/api/billing/balance-sheet"],
  });

  const filteredTransactions = useMemo(() => {
    return transactions.filter((tx) => {
      const matchesSearch = searchQuery === "" || 
        tx.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tx.category?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesType = typeFilter === "all" || tx.type === typeFilter;
      const matchesSource = sourceFilter === "all" || tx.source === sourceFilter;
      
      const txYear = tx.date ? new Date(tx.date).getFullYear().toString() : "";
      const matchesYear = yearFilter === "all" || txYear === yearFilter;
      
      return matchesSearch && matchesType && matchesSource && matchesYear;
    });
  }, [transactions, searchQuery, typeFilter, sourceFilter, yearFilter]);

  const totals = useMemo(() => {
    const income = filteredTransactions
      .filter(tx => tx.type === "income")
      .reduce((sum, tx) => sum + parseFloat(String(tx.amount || "0")), 0);
    
    const expenses = filteredTransactions
      .filter(tx => tx.type === "expense")
      .reduce((sum, tx) => sum + parseFloat(String(tx.amount || "0")), 0);
    
    return {
      income,
      expenses,
      net: income - expenses,
    };
  }, [filteredTransactions]);

  const years = useMemo(() => {
    const yearSet = new Set<string>();
    transactions.forEach((tx) => {
      if (tx.date) {
        yearSet.add(new Date(tx.date).getFullYear().toString());
      }
    });
    return Array.from(yearSet).sort((a, b) => parseInt(b) - parseInt(a));
  }, [transactions]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card data-testid="card-total-income">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Total Income</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600" data-testid="text-total-income">
              {formatCurrency(totals.income)}
            </div>
            <p className="text-xs text-muted-foreground">
              {filteredTransactions.filter(tx => tx.type === "income").length} transactions
            </p>
          </CardContent>
        </Card>

        <Card data-testid="card-total-expenses">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Total Expenses</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600" data-testid="text-total-expenses">
              {formatCurrency(totals.expenses)}
            </div>
            <p className="text-xs text-muted-foreground">
              {filteredTransactions.filter(tx => tx.type === "expense").length} transactions
            </p>
          </CardContent>
        </Card>

        <Card data-testid="card-net-balance">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Net Balance</CardTitle>
            <Wallet className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${totals.net >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="text-net-balance">
              {formatCurrency(totals.net)}
            </div>
            <p className="text-xs text-muted-foreground">
              {filteredTransactions.length} total transactions
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>Balance Sheet Transactions</CardTitle>
              <CardDescription>
                All business transactions synced from Budget App and CRM activities
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isRefetching}
              data-testid="button-refresh-balance-sheet"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-6">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="input-search-transactions"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7"
                  onClick={() => setSearchQuery("")}
                  data-testid="button-clear-search"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="flex gap-2 flex-wrap">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-[130px]" data-testid="select-type-filter">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-full sm:w-[150px]" data-testid="select-source-filter">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="budget_app">Budget App</SelectItem>
                  <SelectItem value="invoice_payment">Invoice Payment</SelectItem>
                  <SelectItem value="commission_payment">Commission</SelectItem>
                  <SelectItem value="accounts_payable">Payable</SelectItem>
                </SelectContent>
              </Select>

              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger className="w-full sm:w-[110px]" data-testid="select-year-filter">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {years.map((year) => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {filteredTransactions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No transactions found</p>
              <p className="text-sm">Connect your Budget App to sync transactions</p>
            </div>
          ) : isMobile ? (
            <div className="space-y-2">
              {filteredTransactions.map((tx) => (
                <div key={tx.id} className="rounded-lg border border-border p-3" data-testid={`row-transaction-${tx.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium truncate flex-1 mr-2">{tx.description}</span>
                    <span className={`font-bold text-sm shrink-0 ${tx.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.type === 'expense' ? '-' : ''}{formatCurrency(tx.amount)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
                    <span>{formatDate(tx.date)}</span>
                    {tx.category && <><span>·</span><span>{tx.category}</span></>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {getTypeBadge(tx.type)}
                    {getSourceBadge(tx.source)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map((tx) => (
                    <TableRow key={tx.id} data-testid={`row-transaction-${tx.id}`}>
                      <TableCell className="font-medium">{formatDate(tx.date)}</TableCell>
                      <TableCell className="max-w-[250px] truncate">{tx.description}</TableCell>
                      <TableCell>{tx.category || "-"}</TableCell>
                      <TableCell>{getTypeBadge(tx.type)}</TableCell>
                      <TableCell>{getSourceBadge(tx.source)}</TableCell>
                      <TableCell className={`text-right font-medium ${tx.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.type === 'expense' ? '-' : ''}{formatCurrency(tx.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
